import React from 'react';
import { useTranslation } from 'react-i18next';
import uuid from 'uuid/v1';
import classNames from 'classnames';

import IconButton from '@material-ui/core/IconButton';
import Typography from '@material-ui/core/Typography';
import Accordion from '@material-ui/core/Accordion';
import AccordionSummary from '@material-ui/core/AccordionSummary';
import AccordionDetails from '@material-ui/core/AccordionDetails';
import Link from '@material-ui/core/Link';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import AddCircleOutlineIcon from '@material-ui/icons/AddCircleOutline';
import LaunchIcon from '@material-ui/icons/Launch';

import ConfirmContext from 'contexts/ConfirmContext';

import { TextIconButton } from 'components';

import { useStyles } from './styles';

const SubTitleLink = ({ getSubTitleLink, fields, index }) => {
  const [t] = useTranslation();
  const classes = useStyles();

  if (!getSubTitleLink) return null;

  const subTitleLink = getSubTitleLink(fields.get(index));
  if (!subTitleLink) return null;

  return (
    <Link
      onClick={e => e.stopPropagation()}
      href={subTitleLink.link}
      variant={'body1'}
      target="_blank"
      className={classes.subTitleLink}
    >
      {t(subTitleLink.title)} <LaunchIcon className={classes.launchIcon} />
    </Link>
  );
};

const ExpansionPanelWrapper = ({
  children,
  isAddDeleteActions,
  disabledItemIndex,
  getTitle,
  getSubTitleLink,
  fields,
  add,
  remove,
  removeAll,
  isHiddenAddNewButton,
  deleteDescription,
  meta,
  tabName,
  tabNameAll
}) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const confirm = React.useContext(ConfirmContext);
  const { error, submitFailed } = meta;

  const [expanded, setExpanded] = React.useState({});

  React.useEffect(() => {
    const allFields = fields.getAll();
    if (allFields && allFields.length) {
      const firstField = fields.get(0);
      setExpanded({ ...expanded, [firstField.id]: true });
    }
  }, []);

  const onExpandHandler = (isExpanded, fieldId) => {
    setExpanded({ ...expanded, [fieldId]: isExpanded });
  };

  const onAddHandler = () => {
    const id = uuid();
    setExpanded({ ...expanded, [id]: true });
    add(fields, id);
  };

  const onDeleteHandler = (e, index) => {
    e.stopPropagation();

    confirm({
      title: `${t('Are you sure you want to delete')} ${t(tabName)}?`,
      description: deleteDescription,
      confirmationText: 'Delete',
      cancellationText: 'Back'
    })
      .then(() => {
        if (remove) remove(fields.get(index));
        fields.remove(index);
      })
      .catch(() => {});
  };

  const onDeleteAllHandler = () => {
    confirm({
      title: `${t('Are you sure you want to delete')} ${t(tabNameAll)}?`,
      description: deleteDescription,
      confirmationText: 'Delete',
      cancellationText: 'Back'
    })
      .then(() => {
        if (disabledItemIndex === 0 || disabledItemIndex) {
          const fieldCopy = fields.get(disabledItemIndex);
          fields.removeAll();
          fields.push(fieldCopy);
        } else {
          if (removeAll) removeAll();
          fields.removeAll();
        }
      })
      .catch(() => {});
  };

  const isDeleteAllBtn = () => {
    if (fields.length - 1 > disabledItemIndex) return true;
    return !!fields.length && disabledItemIndex === undefined;
  };

  const isDeleteBtn = () => {
    if (!isAddDeleteActions) return false;
    if (fields.length > 1) return true;
    return !!fields.length && disabledItemIndex === undefined;
  };

  return (
    <div className={classes.wrapper}>
      {isAddDeleteActions && (
        <div className={classNames(classes.row, classes.buttonsGroup)}>
          {!isHiddenAddNewButton && (
            <TextIconButton
              icon={<AddCircleOutlineIcon />}
              text={t('Add new')}
              click={onAddHandler}
            />
          )}
          {isDeleteAllBtn() && (
            <TextIconButton
              icon={<DeleteOutlineIcon />}
              text={t('Delete all')}
              click={onDeleteAllHandler}
            />
          )}
        </div>
      )}

      {fields.map((field, index) => {
        const fieldObj = fields.get(index);

        return (
          <Accordion
            key={`${index}_${fieldObj.id}`}
            expanded={expanded[fieldObj.id] || false}
            onChange={(event, isExpanded) =>
              onExpandHandler(isExpanded, fieldObj.id)
            }
            classes={{
              root: classNames(classes.root, {
                [classes.error]: submitFailed && error && error[index]
              }),
              expanded: classes.rootExpanded
            }}
          >
            <AccordionSummary
              classes={{
                root: classes.rootSummary,
                content: classes.panelSummaryContent,
                expandIcon: classes.expandMoreIconRoot
              }}
              expandIcon={<ExpandMoreIcon className={classes.expandMoreIcon} />}
              aria-controls="panel1a-content"
              id="panel1a-header"
            >
              <div className={classes.headerContentWrapper}>
                <div>
                  <Typography variant={'body1'} className={classes.heading}>
                    {t(getTitle(fields.get(index)))}
                  </Typography>
                  <SubTitleLink
                    fields={fields}
                    index={index}
                    getSubTitleLink={getSubTitleLink}
                  />
                </div>
                {isDeleteBtn() && (
                  <IconButton
                    aria-label="delete"
                    onClick={e => onDeleteHandler(e, index)}
                  >
                    <DeleteOutlineIcon className={classes.deleteIcon} />
                  </IconButton>
                )}
              </div>
            </AccordionSummary>
            <AccordionDetails classes={{ root: classes.panelDetailsRoot }}>
              {children(field, index, meta, fieldObj)}
            </AccordionDetails>
          </Accordion>
        );
      })}
    </div>
  );
};

export default ExpansionPanelWrapper;
